import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, Switch, Paper,
  Divider, CircularProgress, alpha, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Tooltip, Avatar, Menu, MenuItem, ListItemIcon,
  ListItemText, Tabs, Tab,
} from "@mui/material";
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from "@hello-pangea/dnd";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ImageIcon from "@mui/icons-material/Image";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TvIcon from "@mui/icons-material/Tv";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TimerIcon from "@mui/icons-material/Timer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import StorageIcon from "@mui/icons-material/Storage";
import EventIcon from "@mui/icons-material/Event";
import HistoryIcon from "@mui/icons-material/History";
import {
  useGetPlaylistQuery,
  useUpdatePlaylistMutation,
  useGetMediaQuery,
  useAddPlaylistItemMutation,
  useUpdatePlaylistItemMutation,
  useRemovePlaylistItemMutation,
  useReorderPlaylistItemsMutation,
  useGetTemplatesQuery,
  useGetCompanyQuery,
  useGetPlaylistGroupsQuery,
} from "../../../Api/attoDeskApi";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import AiImageDialog from "../../../Components/Ai/AiImageDialog";
import TransitionEditorPopover, { TransitionDraft } from "./TransitionEditorPopover";
import {
  TRANSITIONS, EASINGS, DEFAULT_DURATION_MS, DEFAULT_EASING,
  transitionLabel,
} from "../../Player/transitions";
import { buildTransitionPlan } from "../../Player/transitionEngine";
import { effectiveTransitionMs, sceneSwitchDelayMs } from "../../Player/playbackEngine";
import { TransitionStage } from "../../Player/TransitionStage";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import {
  IMedia, IPlaylist, IPlaylistItem, IPlaylistGroup,
} from "../../../Api/Interface/api.interface";
import PlaylistGroupSelect from "../../../Components/ui/PlaylistGroupSelect";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import { useUploads } from "../../../Components/Uploads/UploadContext";
import TemplatePreviewDialog, { TemplatePreviewById } from "../../Template/TemplatePreviewDialog";
import MenuTabs from "./MenuTabs";

const DEFAULT_IMAGE_DURATION = 10;
const DURATION_PRESETS = [5, 10, 15, 20, 30, 60];

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatBytes = (b: number): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
};

const formatRelative = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Tiny presentation primitives                                              */
/* ──────────────────────────────────────────────────────────────────────── */

const StatBadge: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactElement;
  tint: string;
  bg: string;
}> = ({ label, value, icon, tint, bg }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: "16px", border: `1px solid ${BORDER}`,
    boxShadow: "0 2px 6px rgba(11,30,45,0.05)", height: "100%", bgcolor: "#fff",
  }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.25 }}>
      <Avatar sx={{ bgcolor: bg, color: tint, width: 38, height: 38 }}>
        {React.cloneElement(icon, { sx: { fontSize: 19 } })}
      </Avatar>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontSize: 26, fontWeight: 800, color: NAVY, lineHeight: 1.1,
      letterSpacing: "-0.02em",
    }}>
      {value}
    </Typography>
  </Paper>
);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    py: 1.25, borderBottom: `1px solid ${BORDER}`,
    "&:last-of-type": { borderBottom: "none" },
  }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
      {value}
    </Typography>
  </Box>
);

const SectionCard: React.FC<{
  icon: React.ReactElement;
  title: string;
  accent: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, accent, subtitle, right, children }) => (
  <Paper elevation={0} sx={{
    mb: 3, borderRadius: 3, border: `1px solid ${BORDER}`,
    overflow: "hidden", bgcolor: "#fff",
  }}>
    <Box sx={{
      background: `linear-gradient(135deg, ${alpha(accent, 0.04)} 0%, ${alpha(accent, 0.01)} 100%)`,
      borderBottom: `1px solid ${alpha(accent, 0.08)}`,
      px: 3, py: 2.5,
      display: "flex", alignItems: "center", gap: 1.5,
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 100%)`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color: accent } })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography fontSize={15} fontWeight={700} color={NAVY} letterSpacing="-0.01em">
          {title}
        </Typography>
        {subtitle && (
          <Typography fontSize={11} color="#94a3b8" mt={0.25}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

const TypeBadge: React.FC<{ type: "image" | "video" }> = ({ type }) => (
  <Chip
    size="small"
    icon={type === "image" ? <ImageIcon sx={{ fontSize: 11 }} /> : <OndemandVideoIcon sx={{ fontSize: 11 }} />}
    label={type === "image" ? "IMAGE" : "VIDEO"}
    sx={{
      height: 20, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em",
      bgcolor: type === "image" ? "#dbeafe" : "#0f172a",
      color: type === "image" ? "#1e40af" : "#fff",
      border: "none",
      "& .MuiChip-icon": {
        color: type === "image" ? "#3b82f6" : "#cbd5e1",
        ml: 0.5,
      },
    }}
  />
);

/**
 * Compact effective-transition summary shown on each row (spec §6) so an
 * operator can confirm Apply-to-all actually updated every item. Resolves the
 * playlist default when the item opts in, mirroring the server/player.
 */
const TransitionBadge: React.FC<{
  item: IPlaylistItem;
  defaultTransition: string;
  defaultDurationMs: number;
}> = ({ item, defaultTransition, defaultDurationMs }) => {
  const useDefault = item.use_playlist_default_transition ?? true;
  const id = useDefault ? defaultTransition : (item.transition_in ?? "cut");
  const ms = useDefault ? defaultDurationMs : (item.transition_duration_ms ?? defaultDurationMs);
  const secs = +(ms / 1000).toFixed(2);
  const label = `${useDefault ? "Default: " : ""}${transitionLabel(id)}${id === "cut" || id === "none" ? "" : ` · ${secs}s`}`;
  return (
    <Chip
      size="small"
      icon={<AutoAwesomeMotionIcon sx={{ fontSize: 11 }} />}
      label={label}
      sx={{
        height: 20, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.02em",
        bgcolor: useDefault ? "#f1f5f9" : alpha("#7c3aed", 0.1),
        color: useDefault ? "#64748b" : "#6d28d9",
        border: `1px solid ${useDefault ? "#e2e8f0" : alpha("#7c3aed", 0.25)}`,
        "& .MuiChip-icon": { color: useDefault ? "#94a3b8" : "#7c3aed", ml: 0.5 },
      }}
    />
  );
};

/** Small full-bleed scene for an item, used by the per-item transition
 *  popover's live "Preview transition" (spec §7). */
const MiniItemScene: React.FC<{ item?: IPlaylistItem }> = ({ item }) => {
  if (!item) return <Box sx={{ width: "100%", height: "100%", bgcolor: "#000" }} />;
  if (item.item_type === "TEMPLATE") {
    return (
      <Box sx={{
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 0.5, color: "#fff",
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      }}>
        <TvIcon sx={{ fontSize: 26, color: GOLD }} />
        <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
          {item.template?.name ?? `Template #${item.template_id ?? ""}`}
        </Typography>
      </Box>
    );
  }
  if (item.media?.media_type === "image") {
    return (
      <Box component="img" src={item.media.url} alt=""
        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    );
  }
  return (
    <Box component="video" src={item.media?.url} muted autoPlay loop playsInline
      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", bgcolor: "#000" }} />
  );
};

const MediaThumb: React.FC<{ media: IMedia; size?: number; rounded?: number }> = ({
  media, size = 80, rounded = 8,
}) => {
  if (media.media_type === "image") {
    return (
      <Box component="img" src={media.url} alt=""
        sx={{
          width: size, height: size * 0.7, objectFit: "cover",
          borderRadius: `${rounded}px`, display: "block",
          bgcolor: "#f1f5f9", flexShrink: 0,
        }} />
    );
  }
  return (
    <Box sx={{
      position: "relative", width: size, height: size * 0.7, borderRadius: `${rounded}px`,
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#fff", flexShrink: 0, overflow: "hidden",
    }}>
      <PlayCircleFilledIcon sx={{ fontSize: size * 0.35, opacity: 0.95 }} />
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Main page                                                                  */
/* ──────────────────────────────────────────────────────────────────────── */

const PlaylistEdit: React.FC<{
  /** When set, edits this playlist instead of the one in the route (modal use). */
  playlistId?: number;
  /** Overrides the back/exit navigation (e.g. close a hosting dialog). */
  onExit?: () => void;
  /** Drops the full-page negative margins when embedded in a dialog. */
  embedded?: boolean;
  /**
   * Legacy/company fallback for the company selector. Used only when the
   * loaded playlist itself has no company_id (e.g. an older row opened from
   * a playlist group that owns the company). Never overrides a playlist's
   * own company_id. Requirement 12: prefer playlist company, fall back to
   * the playlist group's company.
   */
  fallbackCompanyId?: number;
}> = ({ playlistId: playlistIdProp, onExit, embedded = false, fallbackCompanyId }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const playlistId = playlistIdProp ?? Number(id);
  const handleExit = onExit ?? (() => navigate("/playlist/list"));
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetPlaylistQuery(playlistId);
  const [updatePlaylist, { isLoading: isSaving }] = useUpdatePlaylistMutation();
  const [addItem] = useAddPlaylistItemMutation();
  const [updateItem] = useUpdatePlaylistItemMutation();
  const [removeItem] = useRemovePlaylistItemMutation();
  const [reorderItems, { isLoading: isReordering }] = useReorderPlaylistItemsMutation();
  const [publishing, setPublishing] = useState(false);

  // ── State
  // Active editor tab: 0 Content · 1 Schedule · 2 Menu Rules · 3 POS Sync
  // · 4 Screens · 5 History.
  const [tab, setTab] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<IPlaylistItem[]>([]);
  // Unsaved transition edits (per-item overrides + Apply-to-all + default
  // transition changes) are deferred and persisted only on Save Changes /
  // Publish. `dirty` drives the header indicator + the unload guard;
  // pendingItemEditsRef preserves per-item edits across a refetch triggered
  // by a structural op (add / remove / reorder) so they are never stomped.
  const [dirty, setDirty] = useState(false);
  const pendingItemEditsRef = useRef<Map<number, Partial<IPlaylistItem>>>(new Map());
  const [companyId, setCompanyId] = useState<number | "">("");
  // Track whether we've already seeded the company selector from the
  // loaded playlist. Prevents a later re-render of the playlist query
  // (e.g. RTK cache tick after items add/remove/reorder) from clobbering
  // an in-flight edit that hasn't been saved yet. Requirement 5 + 6:
  // "Do not reset the selected company when the company list finishes
  // loading" / "must remain selected after form state refreshes".
  const companyHydratedRef = useRef(false);

  // ── Playlist Group assignment (single group, backed by the
  // playlist_group_items pivot). Seeded once from playlist.playlist_group_id
  // using the real id — never matched by name, never defaulted to the first
  // group. A later re-render (RTK cache tick after an item mutation) must
  // not stomp an unsaved change, so a one-shot ref guards hydration exactly
  // like the company field above.
  const [groupId, setGroupId] = useState<number | "">("");
  const groupHydratedRef = useRef(false);

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  // Only super_admin needs the full companies list. Tenant users see
  // the assigned company as a read-only single-option display, sourced
  // from playlist.company on the loaded row (no extra API call).
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string; is_active?: boolean }> =
    Array.isArray((companiesData as any)?.data)
      ? ((companiesData as any).data as Array<{ id: number; companyName: string; is_active?: boolean }>)
      : [];

  // Playlist groups. Tenant users are already scoped to their own company
  // by the backend; super_admin gets everything, so we filter by the
  // currently-selected company client-side below. The list drives the
  // Playlist Group picker.
  const { data: groupsData } = useGetPlaylistGroupsQuery();
  const allGroups: IPlaylistGroup[] = Array.isArray((groupsData as any)?.data)
    ? ((groupsData as any).data as IPlaylistGroup[])
    : [];

  // ── Modals
  const [pickerOpen, setPickerOpen] = useState(false);
  // "Add Media" dropdown: Upload Image · Upload Video · Media Library · AI.
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [aiOpen, setAiOpen] = useState(false);
  // Presets handed to the media picker so "Upload Image/Video" open the OS
  // file dialog straight away with the matching filter.
  const [pickerInitialType, setPickerInitialType] = useState<"" | "image" | "video">("");
  const [pickerAutoUpload, setPickerAutoUpload] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [durationFor, setDurationFor] = useState<IPlaylistItem | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(null);

  // ── Transitions
  // Playlist-level default item transition (part of Save Changes).
  const [defaultTransition, setDefaultTransition] = useState<string>("cut");
  const [defaultTransitionDurationMs, setDefaultTransitionDurationMs] = useState<number>(DEFAULT_DURATION_MS);
  const [defaultTransitionEasing, setDefaultTransitionEasing] = useState<string>(DEFAULT_EASING);
  const defaultsHydratedRef = useRef(false);
  // Per-item transition popover.
  const [transitionAnchor, setTransitionAnchor] = useState<HTMLElement | null>(null);
  const [transitionItem, setTransitionItem] = useState<IPlaylistItem | null>(null);
  // "Apply to all items" overwrite confirmation (only shown when at least one
  // item currently carries a custom, non-default transition).
  const [confirmApplyAll, setConfirmApplyAll] = useState(false);

  const playlist = data?.data as unknown as IPlaylist | undefined;

  useEffect(() => {
    if (playlist) {
      setName(playlist.name);
      setDescription(playlist.description ?? "");
      setIsActive(playlist.is_active);
      // Seed items from the server but overlay any unsaved per-item transition
      // edits so a refetch from a structural op (add / remove / reorder) never
      // stomps them. New rows appear, deleted rows disappear, order comes from
      // the server; only the operator's pending transition fields are kept.
      const pending = pendingItemEditsRef.current;
      setItems(
        (playlist.items || []).map((it) => {
          const p = pending.get(it.id);
          return p ? ({ ...it, ...p } as IPlaylistItem) : it;
        })
      );
      // One-shot hydration of the company field. See companyHydratedRef
      // comment: re-runs of this effect (fresh playlist payload after
      // an item mutation) must not stomp a pending company change the
      // operator hasn't saved yet.
      if (!companyHydratedRef.current) {
        // Prefer the playlist's own company; fall back to the hosting
        // playlist group's company only when the playlist has none.
        setCompanyId(playlist.company_id ?? fallbackCompanyId ?? "");
        companyHydratedRef.current = true;
      }
      // One-shot hydration of the assigned playlist group from the real id
      // returned by the API. Guarded so media loads / cache ticks never
      // reset an unsaved change (requirement 13).
      if (!groupHydratedRef.current) {
        setGroupId(playlist.playlist_group_id ?? "");
        groupHydratedRef.current = true;
      }
      // One-shot hydration of the playlist default transition (guarded so
      // a cache tick after an item mutation can't stomp an unsaved edit).
      if (!defaultsHydratedRef.current) {
        setDefaultTransition(playlist.default_transition ?? "cut");
        setDefaultTransitionDurationMs(playlist.default_transition_duration_ms ?? DEFAULT_DURATION_MS);
        setDefaultTransitionEasing(playlist.default_transition_easing ?? DEFAULT_EASING);
        defaultsHydratedRef.current = true;
      }
    }
  }, [playlist]);

  // Warn before leaving/refreshing the tab with unsaved transition edits (§3.6).
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ── Aggregates for summary cards
  const stats = useMemo(() => {
    const images = items.filter(i => i.media?.media_type === "image").length;
    const videos = items.filter(i => i.media?.media_type === "video").length;
    const imageDuration = items
      .filter(i => i.media?.media_type === "image")
      .reduce((s, i) => s + (i.duration || DEFAULT_IMAGE_DURATION), 0);
    return { images, videos, imageDuration };
  }, [items]);

  // Merge the playlist's currently-assigned company into the options
  // list even if it isn't in the fetched set (soft-deleted, or the
  // list hasn't hydrated yet). Guarantees the Select can display the
  // seeded value without collapsing back to "empty" during any race.
  const companyOptions = useMemo(() => {
    const list = [...companies];
    const assigned = playlist?.company;
    if (assigned && !list.some((c) => c.id === assigned.id)) {
      list.push({
        id: assigned.id,
        companyName: assigned.name,
        is_active: assigned.is_active,
      });
    }
    return list;
  }, [companies, playlist?.company]);

  const selectedCompany = companyOptions.find((c) => c.id === companyId) ?? null;
  const showInactiveChip = selectedCompany && selectedCompany.is_active === false;

  // Groups that belong to the currently-selected company. Only active
  // groups are offered, but the playlist's currently-assigned group is
  // always merged in (even if inactive) so the selected value never
  // collapses to blank. Requirement 3 + 7: never show another company's
  // groups; keep the selection stable across renders.
  const companyGroups = useMemo(() => {
    const list = allGroups.filter(
      (g) =>
        g.is_active &&
        (companyId === "" || g.company_id == null || g.company_id === companyId)
    );
    const assigned = playlist?.playlist_group;
    if (
      assigned &&
      (companyId === "" || assigned.company_id == null || assigned.company_id === companyId) &&
      !list.some((g) => g.id === assigned.id)
    ) {
      list.push({
        id: assigned.id,
        name: assigned.name,
        playback_mode: assigned.playback_mode,
        is_active: assigned.is_active,
        company_id: assigned.company_id ?? null,
        description: null,
        total_duration: 0,
      });
    }
    return list;
  }, [allGroups, companyId, playlist?.playlist_group]);

  // When the company changes, keep the group selected only if it belongs
  // to the newly-selected company; otherwise reset to "— No Playlist
  // Group —". Requirement 7.
  const handleCompanyChange = (next: number | "") => {
    setCompanyId(next);
    if (groupId !== "") {
      const stillValid = allGroups.some(
        (g) => g.id === groupId && (next === "" || g.company_id == null || g.company_id === next)
      );
      if (!stillValid) setGroupId("");
    }
  };

  // ── Mutations
  const persistMeta = async (overrides?: Partial<{
    name: string; description: string | null; is_active: boolean; company_id: number | null;
  }>) => {
    const body: any = {
      name: (overrides?.name ?? name).trim(),
      description: (overrides?.description !== undefined ? overrides.description : description.trim()) || null,
      is_active: overrides?.is_active ?? isActive,
    };
    // Include company_id in every update. For super_admin this lets
    // them reassign the tenant; for tenant users it round-trips the
    // existing value (server-side controller strips it before fill()
    // so no cross-tenant escape). Requirement 9: "Still include the
    // existing company_id in the update request".
    const effectiveCompanyId =
      overrides?.company_id !== undefined ? overrides.company_id
      : (companyId === "" ? null : Number(companyId));
    if (effectiveCompanyId !== undefined) {
      body.company_id = effectiveCompanyId;
    }
    // Always submit the group assignment so the backend syncs the pivot.
    // null => "— No Playlist Group —". Requirement 11.
    body.playlist_group_id = groupId === "" ? null : Number(groupId);
    // Playlist-level default transition — part of Save Changes.
    body.default_transition = defaultTransition;
    body.default_transition_duration_ms = defaultTransitionDurationMs;
    body.default_transition_easing = defaultTransitionEasing;
    // Per-item transition overrides. Deferred edits (per-item popover +
    // Apply-to-all) live only in local `items` until now; the backend
    // applies them in the same request/version bump. Sent for every item
    // (idempotent) so the single source of truth is round-tripped exactly.
    body.items = items.map((i) => ({
      id: i.id,
      use_playlist_default_transition: i.use_playlist_default_transition ?? true,
      transition_in: i.transition_in ?? null,
      transition_out: i.transition_out ?? null,
      transition_duration_ms: i.transition_duration_ms ?? null,
      transition_easing: i.transition_easing ?? null,
    }));
    return updatePlaylist({ id: playlistId, body }).unwrap();
  };

  const handleSave = async () => {
    try {
      const res = await persistMeta();
      if (res.status) {
        pendingItemEditsRef.current.clear();
        setDirty(false);
        showMessage("Saved");
        refetch();
      }
    } catch { showErrorMessage("Failed to save"); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await persistMeta({ is_active: true });
      if (res.status) {
        pendingItemEditsRef.current.clear();
        setDirty(false);
        setIsActive(true);
        showMessage("Published — devices will pick up the new version");
        refetch();
      }
    } catch { showErrorMessage("Failed to publish"); }
    finally { setPublishing(false); }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setItems(next); // optimistic
    try {
      const res = await reorderItems({
        id: playlistId, item_ids: next.map(i => i.id),
      }).unwrap();
      if (res.status) { showMessage("Order saved"); refetch(); }
    } catch {
      showErrorMessage("Failed to save order");
      refetch();
    }
  };

  const handleAddTemplate = async (templateId: number, durationSeconds: number) => {
    try {
      await addItem({
        id: playlistId,
        item_type: "TEMPLATE",
        template_id: templateId,
        duration: durationSeconds,
      }).unwrap();
      showMessage("Template added");
      setTemplatePickerOpen(false);
      refetch();
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to add template");
    }
  };

  const handleAddBatch = async (mediaItems: IMedia[]) => {
    try {
      for (const m of mediaItems) {
        await addItem({
          id: playlistId,
          media_id: m.id,
          duration: m.media_type === "image" ? DEFAULT_IMAGE_DURATION : null,
        }).unwrap();
      }
      showMessage(`Added ${mediaItems.length} item${mediaItems.length > 1 ? "s" : ""}`);
      refetch();
    } catch { showErrorMessage("Failed to add some items"); }
  };

  // Open the media picker from the "Add Media" dropdown. The two upload
  // options preset the library filter and auto-open the OS file dialog;
  // "Select from Media Library" just browses.
  const openMediaPicker = (type: "" | "image" | "video", autoUpload: boolean) => {
    setPickerInitialType(type);
    setPickerAutoUpload(autoUpload);
    setAddMenuAnchor(null);
    setPickerOpen(true);
  };

  const handleRemove = async (itemId: number) => {
    try {
      const res = await removeItem({ playlistId, itemId }).unwrap();
      if (res.status) { showMessage("Item removed"); refetch(); }
    } catch { showErrorMessage("Failed to remove"); }
  };

  const handleSetDuration = async (item: IPlaylistItem, seconds: number) => {
    // duration lives on playlist_items; updated in place via PUT /items/{id}
    // so the item id, sort order and any transition settings are preserved
    // (the old remove+re-add lost all of those).
    if (!item.media_id) {
      showErrorMessage("Duration is set when the template is added.");
      return;
    }
    // Optimistically reflect the new duration locally so the Preview and the
    // effective-transition badge recompute immediately (spec §2), then persist.
    patchLocalItem(item.id, { duration: seconds });
    try {
      await updateItem({ playlistId, itemId: item.id, body: { duration: seconds } }).unwrap();
      showMessage(`Duration set to ${seconds}s`);
      setDurationFor(null);
      refetch();
    } catch { showErrorMessage("Failed to update duration"); refetch(); }
  };

  // Optimistically patch an item in local state so the row reflects the
  // change immediately, then persist. Keeps unsaved reorder/edits intact.
  const patchLocalItem = (itemId: number, patch: Partial<IPlaylistItem>) =>
    setItems(prev => prev.map(i => (i.id === itemId ? { ...i, ...patch } : i)));

  // Record an unsaved per-item transition edit: patch local state, remember
  // it so a later refetch can't stomp it, and mark the page dirty. Persisted
  // by Save Changes / Publish (spec §3, §11) — never auto-saved.
  const stageItemEdit = (itemId: number, fields: Partial<IPlaylistItem>) => {
    patchLocalItem(itemId, fields);
    const prev = pendingItemEditsRef.current.get(itemId) ?? {};
    pendingItemEditsRef.current.set(itemId, { ...prev, ...fields });
    setDirty(true);
  };

  const handleSaveItemTransition = (item: IPlaylistItem, draft: TransitionDraft) => {
    const useDefault = draft.use_default ?? true;
    stageItemEdit(item.id, {
      use_playlist_default_transition: useDefault,
      transition_in: draft.transition_in ?? "cut",
      transition_out: draft.transition_out ?? null,
      transition_duration_ms: draft.transition_duration_ms ?? DEFAULT_DURATION_MS,
      transition_easing: draft.transition_easing ?? DEFAULT_EASING,
    } as Partial<IPlaylistItem>);
  };

  // Apply the playlist default transition config to every item. When some
  // items carry a custom (non-default) transition, confirm first (spec §4);
  // otherwise apply straight away. Local-state only — persisted on Save.
  const applyDefaultToAllItems = () => {
    setItems((prev) =>
      prev.map((i) => {
        const fields = {
          use_playlist_default_transition: true,
          transition_in: defaultTransition,
          transition_out: null,
          transition_duration_ms: defaultTransitionDurationMs,
          transition_easing: defaultTransitionEasing,
        } as Partial<IPlaylistItem>;
        const prevPending = pendingItemEditsRef.current.get(i.id) ?? {};
        pendingItemEditsRef.current.set(i.id, { ...prevPending, ...fields });
        return { ...i, ...fields } as IPlaylistItem;
      })
    );
    setDirty(true);
    showMessage("Transition applied to all playlist items.");
  };

  const handleApplyDefaultToAll = () => {
    if (items.length === 0) return;
    const hasCustom = items.some(
      (i) => (i.use_playlist_default_transition ?? true) === false
    );
    if (hasCustom) {
      setConfirmApplyAll(true);
      return;
    }
    applyDefaultToAllItems();
  };

  // ── Preview (single shared entry point). Both the header button and the
  // Playlist Items card button call this exact handler and use `canPreview`
  // for their disabled state, so they can never diverge. The preview reads
  // live editor state (items order, durations, transitions, added/removed
  // items) straight from `PreviewDialog`'s props — no save required.
  const canPreview = items.length > 0;
  const handlePreview = () => {
    if (!canPreview) return;
    setPreviewOpen(true);
  };

  if (isLoading || !playlist) return <PageLoader height="20vh" />;

  return (
    <Box sx={{
      bgcolor: "#f1f5f9",
      minHeight: "calc(100vh - 100px)",
      mx: embedded ? 0 : -3,
      mt: embedded ? 0 : -3,
    }}>
      {/* ─── Premium dark header ─── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={handleExit} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>
                {name || "Untitled Playlist"}
              </Typography>
              <Chip size="small" label={`Version ${playlist.version}`}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: "rgba(212,175,55,0.15)", color: GOLD_SOFT,
                  border: `1px solid ${alpha(GOLD, 0.35)}`,
                }} />
              <Chip size="small"
                label={isActive ? "Active" : "Inactive"}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: isActive ? "rgba(16,185,129,0.18)" : "rgba(148,163,184,0.18)",
                  color: isActive ? "#a7f3d0" : "#cbd5e1",
                  border: `1px solid ${isActive ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2.5, mt: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PermMediaIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {items.length} Media Item{items.length === 1 ? "" : "s"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TvIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {playlist.devices_count ?? 0} Device{(playlist.devices_count ?? 0) === 1 ? "" : "s"} Assigned
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }} />
                <Typography fontSize={11.5} color="rgba(255,255,255,0.65)">
                  {formatDuration(stats.imageDuration)} image runtime
                </Typography>
              </Box>
            </Box>
          </Box>
          {dirty && (
            <Chip size="small" label="Unsaved changes"
              sx={{
                height: 24, fontSize: 11, fontWeight: 700,
                bgcolor: "rgba(245,158,11,0.16)", color: "#fcd34d",
                border: "1px solid rgba(245,158,11,0.4)",
              }} />
          )}
          <Button type="button" variant="outlined" size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
            onClick={handlePreview} disabled={!canPreview}
            aria-label="Preview playlist"
            title={canPreview ? "Preview playlist" : "Add an item to preview"}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.18)", color: "#e2e8f0", px: 2.5,
              "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.08)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.25)", borderColor: "rgba(255,255,255,0.08)" },
            }}>
            Preview
          </Button>
          <Button variant="contained" disableElevation size="small"
            startIcon={isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            onClick={handleSave} disabled={isSaving}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            }}>
            {isSaving ? "Saving…" : "Save Changes"}
          </Button>
          <Button variant="contained" disableElevation size="small"
            startIcon={publishing ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <PublishIcon sx={{ fontSize: 16 }} />}
            onClick={handlePublish} disabled={publishing || items.length === 0}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
              "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
            }}>
            Publish
          </Button>
        </Box>
      </Box>

      {/* ─── Smart Menu Scheduling tabs ─── */}
      <Box sx={{ px: 3, pt: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          TabIndicatorProps={{ sx: { backgroundColor: GOLD, height: 3 } }}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700, fontSize: 13, color: TEXT_SECONDARY, minHeight: 42 },
            "& .Mui-selected": { color: NAVY },
          }}>
          <Tab icon={<PermMediaIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Content" />
          <Tab icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Schedule" />
          <Tab icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Menu Rules" />
          <Tab icon={<StorageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="POS Sync" />
          <Tab icon={<TvIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Screens" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="History" />
        </Tabs>
      </Box>

      {/* ─── Content tab (existing editor body) ─── */}
      {tab === 0 && (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatBadge label="Total Images" value={stats.images}
              icon={<ImageIcon />} tint="#3b82f6" bg="#dbeafe" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Total Videos" value={stats.videos}
              icon={<OndemandVideoIcon />} tint="#0f172a" bg="#e2e8f0" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Image Runtime" value={formatDuration(stats.imageDuration)}
              icon={<TimerIcon />} tint="#f59e0b" bg="#fef3c7" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatBadge label="Assigned Devices" value={playlist.devices_count ?? 0}
              icon={<TvIcon />} tint="#7c3aed" bg="#ede9fe" />
          </Grid>
        </Grid>

        {/* ─── Body: 2-column ─── */}
        <Grid container spacing={3}>
          {/* LEFT: information & summary */}
          <Grid item xs={12} lg={4}>
            <SectionCard
              icon={<InfoOutlinedIcon />}
              title="Playlist Information"
              accent="#3b82f6"
              subtitle="Name, description, status"
            >
              {/* Company selector. Editable for super_admin, locked for
                  tenant users (they still see the assignment). Seeded
                  from playlist.company_id — never from name, never from
                  companies[0], never hardcoded. */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 0.75, mb: 0.75,
                }}>
                  <Typography sx={{
                    fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>
                    Company *
                  </Typography>
                  {showInactiveChip && (
                    <Chip
                      label="Inactive"
                      size="small"
                      sx={{
                        height: 18, fontSize: 9, fontWeight: 700,
                        bgcolor: "#fef2f2", color: "#b91c1c",
                        border: "1px solid #fecaca",
                      }}
                    />
                  )}
                </Box>
                <TextField
                  select fullWidth size="small"
                  value={companyId === "" ? "" : String(companyId)}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleCompanyChange(v === "" ? "" : Number(v));
                  }}
                  disabled={!isSuperAdmin}
                  helperText={
                    !isSuperAdmin
                      ? "Only a super admin can reassign a playlist to a different company."
                      : undefined
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                    },
                    "& .MuiFormHelperText-root": { fontSize: 10, ml: 0, mt: 0.5 },
                  }}
                >
                  <MenuItem value="">— Select a company —</MenuItem>
                  {companyOptions.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.companyName}
                      {c.is_active === false && (
                        <Typography component="span" sx={{
                          ml: 1, fontSize: 10, fontWeight: 700,
                          color: "#b91c1c",
                        }}>
                          · inactive
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Playlist Group selector. Sits directly below Company and
                  above Playlist Name. Lists only the selected company's
                  active groups (+ the current assignment) and offers
                  "+ Add New Playlist Group" to users allowed to create
                  groups — the shared PlaylistGroupSelect handles the
                  quick-create modal + re-select. */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  Playlist Group
                </Typography>
                <PlaylistGroupSelect
                  value={groupId}
                  onChange={setGroupId}
                  groups={companyGroups}
                  createInCompanyId={companyId === "" ? null : companyId}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                    },
                  }}
                />
              </Box>

              <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Playlist Name *
              </Typography>
              <TextField fullWidth value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 15, fontWeight: 600, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />

              <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Description
              </Typography>
              <TextField fullWidth size="small" multiline rows={3}
                placeholder="Optional — describe where this plays and when…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />

              {/* Default item transition — inherited by items that opt in.
                  Saved as part of Save Changes; "Apply to all items" makes
                  every item inherit it immediately. */}
              <Box sx={{
                p: 2, mb: 2, borderRadius: 2.5, bgcolor: "#faf5ff",
                border: `1px solid ${alpha("#7c3aed", 0.18)}`,
              }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#6d28d9", mb: 1, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Default Item Transition
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <TextField select size="small" label="Transition" value={defaultTransition}
                    onChange={(e) => { setDefaultTransition(e.target.value); setDirty(true); }}
                    sx={{ flex: "1 1 45%", minWidth: 130, bgcolor: "#fff" }}>
                    {TRANSITIONS.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Duration" value={String(defaultTransitionDurationMs)}
                    onChange={(e) => { setDefaultTransitionDurationMs(Number(e.target.value)); setDirty(true); }}
                    sx={{ flex: "1 1 45%", minWidth: 100, bgcolor: "#fff" }}>
                    {[200, 300, 500, 750, 1000, 1500, 2000, 3000].map((ms) => (
                      <MenuItem key={ms} value={String(ms)}>{ms / 1000}s</MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Easing" value={defaultTransitionEasing}
                    onChange={(e) => { setDefaultTransitionEasing(e.target.value); setDirty(true); }}
                    sx={{ flex: "1 1 45%", minWidth: 130, bgcolor: "#fff" }}>
                    {EASINGS.map((e) => (
                      <MenuItem key={e.id} value={e.id}>{e.label}</MenuItem>
                    ))}
                  </TextField>
                  <Button size="small" variant="outlined"
                    onClick={handleApplyDefaultToAll}
                    sx={{ flex: "1 1 45%", minWidth: 130, textTransform: "none", borderColor: alpha("#7c3aed", 0.4), color: "#6d28d9" }}>
                    Apply to all items
                  </Button>
                </Box>
              </Box>

              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 2, mb: 0.5, borderRadius: 2.5,
                bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
                border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
              }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Active</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                    Devices only receive active playlists
                  </Typography>
                </Box>
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              </Box>
            </SectionCard>

            <SectionCard
              icon={<StorageIcon />}
              title="Library Summary"
              accent="#0e7c9b"
            >
              <InfoRow label="Playlist Version" value={
                <Chip size="small" label={`v${playlist.version}`}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 800,
                    bgcolor: alpha(GOLD, 0.12), color: "#b58a13",
                    border: `1px solid ${alpha(GOLD, 0.35)}`,
                  }} />
              } />
              <InfoRow label="Total Items" value={items.length} />
              <InfoRow label="Images" value={`${stats.images}`} />
              <InfoRow label="Videos" value={`${stats.videos}`} />
              <InfoRow label="Estimated Runtime" value={
                <Tooltip title="Sum of image durations. Videos play to their natural end.">
                  <span>{formatDuration(stats.imageDuration)} <span style={{ color: "#94a3b8", fontWeight: 400 }}>+ videos</span></span>
                </Tooltip>
              } />
              <InfoRow label="Assigned Devices" value={playlist.devices_count ?? 0} />
            </SectionCard>

            <SectionCard
              icon={<HistoryIcon />}
              title="Timestamps"
              accent="#64748b"
            >
              <InfoRow label="Created" value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <EventIcon sx={{ fontSize: 13, color: TEXT_SECONDARY }} />
                  {formatDate(playlist.created_at)}
                </Box>
              } />
              <InfoRow label="Last Updated" value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <AccessTimeIcon sx={{ fontSize: 13, color: TEXT_SECONDARY }} />
                  {formatRelative(playlist.updated_at)}
                </Box>
              } />
            </SectionCard>
          </Grid>

          {/* RIGHT: items */}
          <Grid item xs={12} lg={8}>
            <SectionCard
              icon={<PermMediaIcon />}
              title={`Playlist Items (${items.length})`}
              accent="#7c3aed"
              subtitle="Drag to reorder · click duration to change image timing"
              right={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button type="button" variant="outlined" size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={handlePreview} disabled={!canPreview}
                    aria-label="Preview playlist"
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                      borderColor: BORDER, color: TEXT_PRIMARY,
                      "&:hover": { borderColor: "#7c3aed", color: "#7c3aed", bgcolor: alpha("#7c3aed", 0.04) },
                    }}>
                    Preview
                  </Button>
                  <Button variant="outlined" size="small"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setTemplatePickerOpen(true)}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                      borderColor: BORDER, color: TEXT_PRIMARY,
                      "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.06) },
                    }}>
                    Add Template
                  </Button>
                  <Button variant="outlined" size="small"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setAiOpen(true)}
                    aria-label="Generate image with AI"
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                      borderColor: alpha(GOLD, 0.5), color: NAVY,
                      "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.06) },
                    }}>
                    Generate with AI
                  </Button>
                  <Button variant="contained" disableElevation size="small"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                    onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                    aria-haspopup="true"
                    aria-expanded={Boolean(addMenuAnchor)}
                    sx={{
                      borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                      background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                      "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                    }}>
                    Add Media
                  </Button>
                  <Menu
                    anchorEl={addMenuAnchor}
                    open={Boolean(addMenuAnchor)}
                    onClose={() => setAddMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{ sx: { mt: 0.5, minWidth: 232, borderRadius: 2, boxShadow: "0 8px 28px rgba(15,23,42,0.16)" } }}
                  >
                    <MenuItem onClick={() => openMediaPicker("image", true)}>
                      <ListItemIcon><ImageIcon sx={{ fontSize: 18, color: "#7c3aed" }} /></ListItemIcon>
                      <ListItemText primary="Upload Image" />
                    </MenuItem>
                    <MenuItem onClick={() => openMediaPicker("video", true)}>
                      <ListItemIcon><OndemandVideoIcon sx={{ fontSize: 18, color: "#7c3aed" }} /></ListItemIcon>
                      <ListItemText primary="Upload Video" />
                    </MenuItem>
                    <MenuItem onClick={() => openMediaPicker("", false)}>
                      <ListItemIcon><PermMediaOutlinedIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} /></ListItemIcon>
                      <ListItemText primary="Select from Media Library" />
                    </MenuItem>
                  </Menu>
                </Box>
              }
            >
              {items.length === 0 ? (
                <EmptyState
                  onAdd={() => setPickerOpen(true)}
                  onUpload={() => navigate("/media/library")}
                />
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="playlist-items">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {items.map((item, index) => (
                          <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                            {(p, snap) => (
                              <Box ref={p.innerRef} {...p.draggableProps}
                                sx={{
                                  display: "flex", alignItems: "center", gap: 2, p: 1.5, mb: 1.25,
                                  borderRadius: 2.5,
                                  bgcolor: snap.isDragging ? alpha("#7c3aed", 0.08) : "#fff",
                                  border: `1px solid ${snap.isDragging ? alpha("#7c3aed", 0.4) : BORDER}`,
                                  boxShadow: snap.isDragging
                                    ? `0 12px 28px ${alpha("#7c3aed", 0.22)}`
                                    : "0 1px 2px rgba(11,30,45,0.04)",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    borderColor: alpha("#7c3aed", 0.5),
                                    boxShadow: `0 4px 12px ${alpha(NAVY, 0.08)}`,
                                  },
                                }}>
                                <Box {...p.dragHandleProps}
                                  sx={{
                                    cursor: "grab", color: "#cbd5e1",
                                    "&:active": { cursor: "grabbing" },
                                    "&:hover": { color: "#7c3aed" },
                                  }}>
                                  <DragIndicatorIcon />
                                </Box>
                                <Box sx={{
                                  width: 36, height: 36, borderRadius: "10px",
                                  background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.15)} 0%, ${alpha("#7c3aed", 0.05)} 100%)`,
                                  border: `1px solid ${alpha("#7c3aed", 0.2)}`,
                                  color: "#7c3aed",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 13, fontWeight: 800,
                                }}>
                                  {String(index + 1).padStart(2, "0")}
                                </Box>
                                {item.item_type === "TEMPLATE" ? (
                                  <TemplateRowBody
                                    item={item}
                                    defaultTransition={defaultTransition}
                                    defaultTransitionDurationMs={defaultTransitionDurationMs}
                                    onPreview={() => setPreviewTemplateId(item.template_id ?? null)}
                                    onEditTransition={(e) => { setTransitionItem(item); setTransitionAnchor(e.currentTarget); }}
                                    onRemove={() => handleRemove(item.id)}
                                  />
                                ) : (
                                  <>
                                    {item.media && <MediaThumb media={item.media} size={104} />}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{
                                        fontSize: 14, fontWeight: 700, color: NAVY,
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                      }}>
                                        {item.media?.original_name || "—"}
                                      </Typography>
                                      <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.75, flexWrap: "wrap" }}>
                                        {item.media && <TypeBadge type={item.media.media_type} />}
                                        {item.media?.media_type === "image" ? (
                                          <Chip size="small"
                                            icon={<TimerIcon sx={{ fontSize: 12 }} />}
                                            label={`${item.duration || DEFAULT_IMAGE_DURATION}s`}
                                            onClick={() => setDurationFor(item)}
                                            sx={{
                                              height: 22, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                              bgcolor: "#fef3c7", color: "#92400e",
                                              border: "1px solid #fcd34d",
                                              "& .MuiChip-icon": { color: "#d97706", ml: 0.5 },
                                              "&:hover": { bgcolor: "#fde68a" },
                                            }} />
                                        ) : (
                                          <Chip size="small"
                                            icon={<PlayArrowIcon sx={{ fontSize: 12 }} />}
                                            label="plays to end"
                                            sx={{
                                              height: 22, fontSize: 11, fontWeight: 700,
                                              bgcolor: "#e0e7ff", color: "#3730a3",
                                              border: "1px solid #c7d2fe",
                                              "& .MuiChip-icon": { color: "#4f46e5", ml: 0.5 },
                                            }} />
                                        )}
                                        {item.media?.file_size != null && (
                                          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                                            {formatBytes(item.media.file_size)}
                                          </Typography>
                                        )}
                                        <TransitionBadge
                                          item={item}
                                          defaultTransition={defaultTransition}
                                          defaultDurationMs={defaultTransitionDurationMs}
                                        />
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                      <Tooltip title="Preview from this item">
                                        <IconButton size="small"
                                          onClick={handlePreview}
                                          sx={{
                                            color: GOLD,
                                            "&:hover": { bgcolor: alpha(GOLD, 0.08) },
                                          }}>
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {item.media?.media_type === "image" && (
                                        <Tooltip title="Edit display duration">
                                          <IconButton size="small"
                                            onClick={() => setDurationFor(item)}
                                            sx={{
                                              color: "#f59e0b",
                                              "&:hover": { bgcolor: "#fef3c7" },
                                            }}>
                                            <TimerIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="Edit transition">
                                        <IconButton size="small"
                                          onClick={(e) => { setTransitionItem(item); setTransitionAnchor(e.currentTarget); }}
                                          sx={{
                                            color: (item.use_playlist_default_transition ?? true) ? "#94a3b8" : "#7c3aed",
                                            "&:hover": { bgcolor: alpha("#7c3aed", 0.08) },
                                          }}>
                                          <AutoAwesomeMotionIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Remove from playlist">
                                        <IconButton size="small"
                                          onClick={() => handleRemove(item.id)}
                                          sx={{
                                            color: "#94a3b8",
                                            "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
                                          }}>
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {isReordering && (
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1, textAlign: "right" }}>
                  Saving order…
                </Typography>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
      )}

      {/* ─── Schedule / Menu Rules / POS / Screens / History tabs ─── */}
      {tab !== 0 && (
        <Box sx={{ p: 3 }}>
          <MenuTabs
            tab={tab}
            playlistId={playlistId}
            playlist={playlist}
            groupId={groupId === "" ? null : Number(groupId)}
            companyId={companyId === "" ? null : Number(companyId)}
          />
        </Box>
      )}

      {/* ─── Modals ─── */}
      <AddMediaDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={async (m) => { await handleAddBatch(m); setPickerOpen(false); }}
        companyId={companyId === "" ? undefined : Number(companyId)}
        // Super admins own no company, so uploads must carry an explicit
        // company_id. When one hasn't been resolved yet, block the upload
        // and send the operator back to the form to pick a company.
        requireCompany={isSuperAdmin}
        onRequireCompany={() => {
          setPickerOpen(false);
          showErrorMessage("Please select a company before uploading media.");
        }}
        initialType={pickerInitialType}
        autoUpload={pickerAutoUpload}
      />
      <AiImageDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        isSuperAdmin={isSuperAdmin}
        companies={companies}
        defaultCompanyId={companyId === "" ? "" : Number(companyId)}
        playlistId={playlistId}
        imageDurationSeconds={DEFAULT_IMAGE_DURATION}
        onAddedToPlaylist={() => refetch()}
      />
      <PreviewDialog
        open={previewOpen}
        items={items}
        onClose={() => setPreviewOpen(false)}
        playlistName={name}
        defaultTransition={defaultTransition}
        defaultTransitionDurationMs={defaultTransitionDurationMs}
        defaultTransitionEasing={defaultTransitionEasing}
      />
      <TransitionEditorPopover
        open={!!transitionItem}
        anchorEl={transitionAnchor}
        mode="item"
        defaultLabel="Use playlist default"
        value={transitionItem ? {
          transition_in: transitionItem.transition_in ?? "cut",
          transition_out: transitionItem.transition_out ?? null,
          transition_duration_ms: transitionItem.transition_duration_ms ?? defaultTransitionDurationMs,
          transition_easing: transitionItem.transition_easing ?? defaultTransitionEasing,
          use_default: transitionItem.use_playlist_default_transition ?? true,
        } : {}}
        previewNodes={transitionItem ? (() => {
          const idx = items.findIndex((i) => i.id === transitionItem.id);
          // Selected item → next item; wrap to the first item when last (§7).
          const nextItem = items.length ? items[(idx + 1) % items.length] : undefined;
          return {
            current: <MiniItemScene item={transitionItem} />,
            next: <MiniItemScene item={nextItem} />,
          };
        })() : undefined}
        itemDurationSeconds={
          transitionItem && (transitionItem.item_type === "TEMPLATE" || transitionItem.media?.media_type === "image")
            ? (transitionItem.duration ?? DEFAULT_IMAGE_DURATION)
            : undefined
        }
        onSave={(draft) => { if (transitionItem) handleSaveItemTransition(transitionItem, draft); }}
        onClose={() => { setTransitionItem(null); setTransitionAnchor(null); }}
      />
      <DurationDialog
        open={!!durationFor}
        currentDuration={durationFor?.duration ?? DEFAULT_IMAGE_DURATION}
        onClose={() => setDurationFor(null)}
        onConfirm={(s) => durationFor && handleSetDuration(durationFor, s)}
      />
      {/* Overwrite confirmation for "Apply to all items" (spec §4) — only
          reached when at least one item has a custom transition. */}
      <Dialog open={confirmApplyAll} onClose={() => setConfirmApplyAll(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY }}>
          Apply transition to all items?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
            This will replace the custom transition settings on all playlist items.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmApplyAll(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation
            onClick={() => { setConfirmApplyAll(false); applyDefaultToAllItems(); }}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            Apply to All
          </Button>
        </DialogActions>
      </Dialog>
      <AddTemplateDialog
        open={templatePickerOpen}
        companyId={companyId === "" ? undefined : Number(companyId)}
        onClose={() => setTemplatePickerOpen(false)}
        onConfirm={handleAddTemplate}
      />
      <TemplatePreviewDialog
        templateId={previewTemplateId}
        onClose={() => setPreviewTemplateId(null)}
      />
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Template picker dialog (V2)                                                */
/* ──────────────────────────────────────────────────────────────────────── */
const AddTemplateDialog: React.FC<{
  open: boolean;
  /** Playlist's selected company. Scopes the template list so operators
   *  only pick templates available to this company (requirement 11). */
  companyId?: number;
  onClose: () => void;
  onConfirm: (templateId: number, durationSeconds: number) => Promise<void>;
}> = ({ open, companyId, onClose, onConfirm }) => {
  // Scope the query to the playlist's company so cross-tenant templates
  // never appear. The backend forCompany scope also keeps null-company
  // "system" templates available to every company.
  const { data, isFetching } = useGetTemplatesQuery(
    companyId ? { company_id: companyId } : undefined,
    { skip: !open },
  );
  const templates: Array<{ id: number; name: string; orientation: string; is_active: boolean }> =
    ((data as any)?.data ?? []).filter((t: any) => t.is_active);

  // Selected template's real database id ("" = nothing picked). Duration
  // kept as a string so the field can be emptied and validated inline
  // rather than snapping to a forced minimum on every keystroke.
  const [selected, setSelected] = useState<number | "">("");
  const [duration, setDuration] = useState<string>("15");
  const [busy, setBusy] = useState(false);

  // Reset all state on open so a previous run's selection/validation never
  // leaks in. Never auto-select a template (requirement 12).
  useEffect(() => {
    if (open) {
      setSelected(""); setDuration("15"); setBusy(false);
    }
  }, [open]);

  // Duration is valid only as a whole positive number of seconds.
  const durationNum = Number(duration);
  const durationValid =
    duration.trim() !== "" && Number.isFinite(durationNum) &&
    Number.isInteger(durationNum) && durationNum > 0;
  const durationError = duration.trim() !== "" && !durationValid;
  const canSubmit = !busy && selected !== "" && durationValid;

  const handle = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onConfirm(Number(selected), durationNum);
    } finally {
      setBusy(false);
    }
  };

  // Shared field styling — matches the Company / Playlist Group selectors
  // on the main form so the modal reads as part of the same application.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5, fontSize: 13, bgcolor: "#fff", minHeight: 44,
      "& fieldset": { borderColor: "#e2e8f0" },
      "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
      "&.Mui-error fieldset": { borderColor: "#ef4444" },
    },
    "& .MuiFormHelperText-root": { fontSize: 10, ml: 0, mt: 0.5 },
  } as const;

  const labelSx = {
    fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75,
    textTransform: "uppercase", letterSpacing: "0.04em",
  } as const;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add Template</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        {isFetching && <Typography sx={{ color: TEXT_SECONDARY, fontSize: 13 }}>Loading…</Typography>}
        {!isFetching && templates.length === 0 && (
          <Typography sx={{ color: TEXT_SECONDARY, fontSize: 13 }}>
            No active templates for this company. Create one in <strong>Templates</strong> first.
          </Typography>
        )}
        {!isFetching && templates.length > 0 && (
          <>
            {/* Template — label above, non-floating, so the label and the
                placeholder can never overlap inside the select. */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={labelSx}>Template *</Typography>
              <TextField
                select fullWidth size="small"
                autoFocus
                value={selected === "" ? "" : String(selected)}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelected(v === "" ? "" : Number(v));
                }}
                sx={fieldSx}
              >
                <MenuItem value="">— Select a template —</MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name} ({t.orientation})
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Duration — label above the input, inline validation that does
                not shift the surrounding layout. */}
            <Box>
              <Typography sx={labelSx}>Duration (seconds)</Typography>
              <TextField
                fullWidth size="small" type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                error={durationError}
                helperText={durationError ? "Enter a whole number of seconds greater than 0." : " "}
                inputProps={{ min: 1, step: 1 }}
                sx={fieldSx}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="secondary"
          onClick={handle} disabled={!canSubmit}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Template playlist-item row (V2)                                            */
/* ──────────────────────────────────────────────────────────────────────── */

/**
 * Render a TEMPLATE-typed playlist item. Drop-in replacement for the
 * existing media row's contents — sits inside the same draggable outer
 * Box, so the drag handle and index badge supplied by the parent
 * still wrap it.
 */
const TemplateRowBody: React.FC<{
  item: IPlaylistItem;
  defaultTransition: string;
  defaultTransitionDurationMs: number;
  onPreview: () => void;
  onEditTransition: (e: React.MouseEvent<HTMLElement>) => void;
  onRemove: () => void;
}> = ({ item, defaultTransition, defaultTransitionDurationMs, onPreview, onEditTransition, onRemove }) => {
  const t = item.template;
  const name = t?.name ?? `Template #${item.template_id ?? "?"}`;
  const orientation = t?.orientation;
  return (
    <>
      {/* Thumb — template icon block instead of a media image. Same
          dimensions as MediaThumb so the row layout matches. */}
      <Box sx={{
        width: 104, height: 70, borderRadius: 1.5, flexShrink: 0,
        background: `linear-gradient(135deg, ${alpha(GOLD, 0.18)} 0%, ${alpha(NAVY, 0.85)} 100%)`,
        border: `1px solid ${alpha(GOLD, 0.5)}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", inset: 6,
          border: "1px dashed rgba(255,255,255,0.35)",
          borderRadius: 0.75,
        }} />
        <TvIcon sx={{ fontSize: 22, color: GOLD, zIndex: 1 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 14, fontWeight: 700, color: NAVY,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {name}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.75, flexWrap: "wrap" }}>
          <Chip size="small" label="TEMPLATE"
            sx={{
              height: 22, fontSize: 11, fontWeight: 700,
              bgcolor: alpha(GOLD, 0.18),
              color: NAVY,
              border: `1px solid ${alpha(GOLD, 0.5)}`,
              letterSpacing: "0.06em",
            }}
          />
          <Chip size="small"
            icon={<TimerIcon sx={{ fontSize: 12 }} />}
            label={`${item.duration ?? 0}s`}
            sx={{
              height: 22, fontSize: 11, fontWeight: 700,
              bgcolor: "#fef3c7", color: "#92400e",
              border: "1px solid #fcd34d",
              "& .MuiChip-icon": { color: "#d97706", ml: 0.5 },
            }}
          />
          {orientation && (
            <Typography sx={{
              fontSize: 11, color: TEXT_SECONDARY, textTransform: "capitalize",
            }}>
              {orientation}
            </Typography>
          )}
          <TransitionBadge
            item={item}
            defaultTransition={defaultTransition}
            defaultDurationMs={defaultTransitionDurationMs}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Preview template">
          <IconButton size="small" onClick={onPreview}
            sx={{
              color: GOLD,
              "&:hover": { bgcolor: alpha(GOLD, 0.08) },
            }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit transition">
          <IconButton size="small" onClick={onEditTransition}
            sx={{
              color: (item.use_playlist_default_transition ?? true) ? "#94a3b8" : "#7c3aed",
              "&:hover": { bgcolor: alpha("#7c3aed", 0.08) },
            }}>
            <AutoAwesomeMotionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove from playlist">
          <IconButton size="small" onClick={onRemove}
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
            }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Empty state                                                                */
/* ──────────────────────────────────────────────────────────────────────── */

const EmptyState: React.FC<{ onAdd: () => void; onUpload: () => void }> = ({ onAdd, onUpload }) => (
  <Box sx={{
    py: 7, px: 3, textAlign: "center",
    border: `2px dashed ${BORDER}`, borderRadius: 3, bgcolor: "#f8fafc",
  }}>
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <svg width="160" height="100" viewBox="0 0 160 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="screen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#F5D97E" />
          </linearGradient>
        </defs>
        <rect x="20" y="14" width="120" height="70" rx="7" fill="url(#screen)" />
        <rect x="26" y="20" width="108" height="58" rx="3" fill="#0b1220" />
        <circle cx="80" cy="49" r="14" fill="url(#gold)" />
        <polygon points="76,42 76,56 89,49" fill="#0f172a" />
        <rect x="68" y="86" width="24" height="3" rx="1.5" fill="#334155" />
        <rect x="58" y="91" width="44" height="5" rx="2.5" fill="#334155" />
        <circle cx="36" cy="32" r="2.5" fill="#7c3aed" />
        <circle cx="124" cy="32" r="2.5" fill="#3b82f6" />
        <circle cx="36" cy="66" r="2.5" fill="#10b981" />
        <circle cx="124" cy="66" r="2.5" fill="#ef4444" />
      </svg>
    </Box>
    <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mb: 0.5, letterSpacing: "-0.01em" }}>
      No media added yet
    </Typography>
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 3, maxWidth: 400, mx: "auto" }}>
      Add images and videos from your library, or upload new files to start building this playlist.
    </Typography>
    <Box sx={{ display: "flex", gap: 1.25, justifyContent: "center" }}>
      <Button variant="contained" disableElevation
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={onAdd}
        sx={{
          borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
          "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
        }}>
        Add Media
      </Button>
      <Button variant="outlined"
        startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
        onClick={onUpload}
        sx={{
          borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
          borderColor: BORDER, color: NAVY,
          "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
        }}>
        Upload Media
      </Button>
    </Box>
  </Box>
);

/* ──────────────────────────────────────────────────────────────────────── */
/* Full-screen Add Media modal                                                */
/* ──────────────────────────────────────────────────────────────────────── */

const AddMediaDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (media: IMedia[]) => Promise<void>;
  /** Company the media library is scoped to and uploads are tagged with. */
  companyId?: number;
  /** When true (super admin), a company_id must exist before uploading. */
  requireCompany?: boolean;
  /** Called when an upload is blocked because no company is resolved. */
  onRequireCompany?: () => void;
  /** Preset the library type filter when opened from the Add Media menu. */
  initialType?: "" | "image" | "video";
  /** Open the OS file dialog immediately (for the "Upload …" menu options). */
  autoUpload?: boolean;
}> = ({ open, onClose, onConfirm, companyId, requireCompany = false, onRequireCompany, initialType = "", autoUpload = false }) => {
  const [search, setSearch] = useState("");
  const [type, setType] = useState<"" | "image" | "video">(initialType);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showMessage, showErrorMessage } = useNotifier();
  const { enqueue, hasActive } = useUploads();

  // Scope the library to the selected company so media from other tenants
  // is never shown (requirement 13).
  const { data, refetch } = useGetMediaQuery({ searchText: search, type: type || undefined, company_id: companyId });
  const media: IMedia[] = useMemo(
    () => Array.isArray(data?.data) ? ((data?.data as unknown) as IMedia[]) : [],
    [data]
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setSearch("");
      setType("");
      return;
    }
    // Seed the filter from the menu choice; for the "Upload …" options, open
    // the OS file dialog right away with the matching accept filter.
    setType(initialType);
    if (autoUpload) {
      const t = setTimeout(() => fileInputRef.current?.click(), 150);
      return () => clearTimeout(t);
    }
  }, [open, initialType, autoUpload]);

  // Accept filter for the hidden upload input — narrowed for Upload Image /
  // Upload Video, both kinds for the general library uploader.
  const uploadAccept =
    initialType === "image" ? ".jpg,.jpeg,.png,.webp"
    : initialType === "video" ? ".mp4"
    : ".jpg,.jpeg,.png,.webp,.mp4";

  const toggle = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selected = media.filter(m => selectedIds.has(m.id));

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Block the upload before it reaches the API when a super admin has no
    // company resolved — otherwise the backend rejects it with a raw
    // "company_id is required" error (requirement 14).
    if (requireCompany && !companyId) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      onRequireCompany?.();
      return;
    }
    const results = await Promise.allSettled(
      Array.from(files).map(f => enqueue(f, companyId))
    );
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected");
    if (ok > 0) {
      showMessage(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
      refetch();
    }
    failed.forEach((r: any) => {
      const msg = r.reason?.message || "Upload failed";
      if (msg && !msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ sx: { bgcolor: "#f1f5f9" } }}>
      {/* Dark header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 2, display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
        }}>
          <PermMediaIcon sx={{ fontSize: 18, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={17} fontWeight={800} color="#fff">
            Add Media to Playlist
          </Typography>
          <Typography fontSize={11} color="rgba(255,255,255,0.55)">
            Select media from your library or upload new files
          </Typography>
        </Box>
        <Chip size="small"
          label={`${selectedIds.size} selected`}
          sx={{
            height: 26, fontSize: 12, fontWeight: 700,
            bgcolor: selectedIds.size > 0 ? "rgba(212,175,55,0.18)" : "rgba(255,255,255,0.08)",
            color: selectedIds.size > 0 ? GOLD_SOFT : "rgba(255,255,255,0.55)",
            border: `1px solid ${selectedIds.size > 0 ? alpha(GOLD, 0.4) : "rgba(255,255,255,0.12)"}`,
            px: 1,
          }} />
        <Button variant="outlined" size="small" onClick={onClose}
          sx={{
            borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
            borderColor: "rgba(255,255,255,0.18)", color: "#e2e8f0", px: 2.5,
            "&:hover": { borderColor: "rgba(255,255,255,0.35)", bgcolor: "rgba(255,255,255,0.06)" },
          }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation size="small"
          disabled={selectedIds.size === 0}
          onClick={() => onConfirm(selected)}
          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
            color: NAVY_DARK,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
            "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.25)" },
          }}>
          Add to Playlist
        </Button>
      </Box>

      <DialogContent sx={{ p: 0, display: "flex", overflow: "hidden" }}>
        {/* Library */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, alignItems: "center" }}>
            <TextField size="small" placeholder="Search media…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
              }}
              sx={{
                width: 320,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                  "& fieldset": { borderColor: BORDER },
                  "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
                },
              }} />
            {[
              { v: "", label: "All", icon: <PermMediaIcon sx={{ fontSize: 14 }} /> },
              { v: "image", label: "Images", icon: <ImageIcon sx={{ fontSize: 14 }} /> },
              { v: "video", label: "Videos", icon: <OndemandVideoIcon sx={{ fontSize: 14 }} /> },
            ].map(opt => (
              <Button key={opt.v} size="small" startIcon={opt.icon}
                onClick={() => setType(opt.v as any)}
                sx={{
                  textTransform: "none", fontSize: 12, fontWeight: 600,
                  borderRadius: 1.5, px: 1.75, border: "1px solid",
                  borderColor: type === opt.v ? GOLD : BORDER,
                  bgcolor: type === opt.v ? alpha(GOLD, 0.08) : "#fff",
                  color: type === opt.v ? NAVY : TEXT_SECONDARY,
                  "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
                }}>
                {opt.label}
              </Button>
            ))}
            <Box sx={{ flex: 1 }} />
            {media.length > 0 && (() => {
              const allSelected = media.every(m => selectedIds.has(m.id));
              return (
                <Button size="small" variant="outlined"
                  startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setSelectedIds(prev => {
                      if (allSelected) {
                        const next = new Set(prev);
                        media.forEach(m => next.delete(m.id));
                        return next;
                      }
                      const next = new Set(prev);
                      media.forEach(m => next.add(m.id));
                      return next;
                    });
                  }}
                  sx={{
                    borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                    borderColor: allSelected ? "#7c3aed" : BORDER,
                    color: allSelected ? "#7c3aed" : TEXT_PRIMARY,
                    bgcolor: allSelected ? alpha("#7c3aed", 0.06) : "#fff",
                    "&:hover": { borderColor: "#7c3aed", bgcolor: alpha("#7c3aed", 0.08), color: "#7c3aed" },
                  }}>
                  {allSelected ? `Deselect All (${media.length})` : `Select All (${media.length})`}
                </Button>
              );
            })()}
            <input ref={fileInputRef} type="file" multiple hidden
              accept={uploadAccept}
              onChange={(e) => handleUpload(e.target.files)} />
            <Button size="small" variant="outlined"
              startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600,
                borderColor: BORDER, color: TEXT_PRIMARY,
                "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
              }}>
              {hasActive ? "Uploading…" : "Upload"}
            </Button>
          </Box>

          {media.length === 0 ? (
            <Box sx={{
              py: 8, textAlign: "center", border: `2px dashed ${BORDER}`,
              borderRadius: 3, bgcolor: "#fff",
            }}>
              <PermMediaIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: 0.5 }}>
                No media in your library
              </Typography>
              <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                Upload images or videos to get started.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {media.map(m => {
                const isSel = selectedIds.has(m.id);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={m.id}>
                    <Paper elevation={0} onClick={() => toggle(m.id)}
                      sx={{
                        position: "relative", cursor: "pointer",
                        borderRadius: 3, overflow: "hidden", bgcolor: "#fff",
                        border: `2px solid ${isSel ? "#7c3aed" : BORDER}`,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: isSel ? "#7c3aed" : alpha("#7c3aed", 0.4),
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 22px ${alpha(NAVY, 0.1)}`,
                        },
                      }}>
                      <Box sx={{ position: "relative" }}>
                        {m.media_type === "image" ? (
                          <Box component="img" src={m.url} alt=""
                            sx={{ width: "100%", height: 130, objectFit: "cover", display: "block", bgcolor: "#f1f5f9" }} />
                        ) : (
                          <Box sx={{
                            height: 130,
                            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#fff",
                          }}>
                            <PlayCircleFilledIcon sx={{ fontSize: 44, opacity: 0.95 }} />
                          </Box>
                        )}
                        <Box sx={{
                          position: "absolute", top: 8, left: 8, zIndex: 1,
                        }}>
                          <TypeBadge type={m.media_type} />
                        </Box>
                        <Box sx={{
                          position: "absolute", top: 8, right: 8,
                          width: 24, height: 24, borderRadius: "50%",
                          border: `2px solid ${isSel ? "#7c3aed" : "rgba(255,255,255,0.85)"}`,
                          bgcolor: isSel ? "#7c3aed" : "rgba(15,23,42,0.55)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "all 0.15s ease",
                          boxShadow: isSel ? "0 2px 8px rgba(124,58,237,0.45)" : "none",
                        }}>
                          {isSel && <CheckCircleIcon sx={{ fontSize: 18, color: "#fff" }} />}
                        </Box>
                      </Box>
                      <Box sx={{ p: 1.25 }}>
                        <Typography sx={{
                          fontSize: 12, fontWeight: 700, color: NAVY,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {m.original_name}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, mt: 0.25 }}>
                          {formatBytes(m.file_size)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* Selected panel */}
        <Box sx={{
          width: 320, borderLeft: `1px solid ${BORDER}`, bgcolor: "#fff",
          display: "flex", flexDirection: "column",
        }}>
          <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Selected Media
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, mt: 0.25, lineHeight: 1.2 }}>
              {selectedIds.size}
            </Typography>
            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
              {selectedIds.size === 0
                ? "Click items on the left to add them"
                : `${selectedIds.size} item${selectedIds.size === 1 ? "" : "s"} ready to add`}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            {selected.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <PermMediaIcon sx={{ fontSize: 32, color: "#cbd5e1", mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
                  Nothing selected yet
                </Typography>
              </Box>
            ) : selected.map(m => (
              <Box key={m.id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 1, mb: 0.5, borderRadius: 2,
                "&:hover": { bgcolor: "#f8fafc" },
              }}>
                <MediaThumb media={m} size={56} rounded={6} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: 12, fontWeight: 700, color: NAVY,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {m.original_name}
                  </Typography>
                  <TypeBadge type={m.media_type} />
                </Box>
                <IconButton size="small" onClick={() => toggle(m.id)}
                  sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
            <Button fullWidth variant="contained" disableElevation
              disabled={selectedIds.size === 0}
              onClick={() => onConfirm(selected)}
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, py: 1.25,
                color: NAVY_DARK,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
                "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" },
              }}>
              Add {selectedIds.size > 0 ? `${selectedIds.size} ` : ""}to Playlist
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Preview dialog — simulates playback                                       */
/* ──────────────────────────────────────────────────────────────────────── */

const PreviewDialog: React.FC<{
  open: boolean;
  items: IPlaylistItem[];
  onClose: () => void;
  playlistName: string;
  defaultTransition?: string;
  defaultTransitionDurationMs?: number;
  defaultTransitionEasing?: string;
}> = ({ open, items, onClose, playlistName,
        defaultTransition = "cut",
        defaultTransitionDurationMs = DEFAULT_DURATION_MS,
        defaultTransitionEasing = DEFAULT_EASING }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // False until the first advance so the item shown when the dialog opens
  // does not reserve time for an incoming transition (TransitionStage paints
  // the opening scene instantly). Stays true after a wrap-around so the
  // last→first transition still animates (spec §10).
  const hasAdvancedRef = useRef(false);
  // Guards against a video's rapid timeupdate events advancing more than once
  // before the index actually changes.
  const advanceLockRef = useRef(false);

  useEffect(() => {
    if (open) {
      setIndex(0); setProgress(0); setPaused(false);
      hasAdvancedRef.current = false; advanceLockRef.current = false;
    }
  }, [open]);
  useEffect(() => { advanceLockRef.current = false; }, [index]);
  const current = items[index];

  // Resolve an item's effective transition, mirroring the server/player
  // resolution (playlist default when the item opts in).
  const resolveEff = React.useCallback((it?: IPlaylistItem) => {
    if (!it || (it.use_playlist_default_transition ?? true)) {
      return { id: defaultTransition || "cut", ms: defaultTransitionDurationMs, easing: defaultTransitionEasing };
    }
    return {
      id: it.transition_in || "cut",
      ms: it.transition_duration_ms ?? defaultTransitionDurationMs,
      easing: it.transition_easing ?? defaultTransitionEasing,
    };
  }, [defaultTransition, defaultTransitionDurationMs, defaultTransitionEasing]);

  const effTransition = resolveEff(current);

  const prevItem = items.length ? items[(index - 1 + items.length) % items.length] : undefined;
  const involvesVideo =
    current?.media?.media_type === "video" || prevItem?.media?.media_type === "video";
  const previewPlan = useMemo(
    () => buildTransitionPlan(effTransition.id, effTransition.easing, effTransition.ms, { involvesVideo }),
    [effTransition.id, effTransition.easing, effTransition.ms, involvesVideo],
  );

  const isTimed = !!current && (current.item_type === "TEMPLATE" || current.media?.media_type === "image");

  const advance = React.useCallback(() => {
    if (advanceLockRef.current) return;
    advanceLockRef.current = true;
    // Mute the outgoing video so its tail doesn't double up with the incoming
    // item's audio during the transition overlap (spec §9).
    if (current?.media?.media_type === "video" && videoRef.current) {
      videoRef.current.muted = true;
    }
    hasAdvancedRef.current = true;
    setIndex(i => (i + 1) % items.length);
    setProgress(0);
  }, [current, items.length]);

  // Image/template timing (spec §8): the item stays active for its full dwell;
  // the transition INTO the next item plays during this item's final window so
  // the next item is fully active exactly at the dwell boundary — the
  // transition is never added on top of the item duration.
  useEffect(() => {
    if (!open || !current || paused || !isTimed) return;
    const dwell = (current.duration || DEFAULT_IMAGE_DURATION) * 1000;
    const tIn = hasAdvancedRef.current
      ? effectiveTransitionMs(effTransition.id, effTransition.ms, dwell)
      : 0;
    const nextEff = resolveEff(items[(index + 1) % items.length]);
    const tNext = effectiveTransitionMs(nextEff.id, nextEff.ms, dwell);
    const delay = sceneSwitchDelayMs(dwell, tIn, tNext);
    const totalWindow = Math.max(1, tIn + dwell);
    const start = Date.now();
    const tick = setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - start) / totalWindow) * 100));
    }, 100);
    const advanceTimer = setTimeout(advance, delay);
    return () => { clearInterval(tick); clearTimeout(advanceTimer); };
  }, [open, index, current, paused, isTimed, items, effTransition.id, effTransition.ms, resolveEff, advance]);

  // Video timing: begin the transition into the next item `tNext` before the
  // video ends so it finishes at the natural end; onEnded is the fallback.
  const handleVideoTime = () => {
    const el = videoRef.current;
    if (!el || paused || !el.duration || !isFinite(el.duration)) return;
    const nextEff = resolveEff(items[(index + 1) % items.length]);
    const tNext = effectiveTransitionMs(nextEff.id, nextEff.ms, el.duration * 1000);
    if ((el.duration - el.currentTime) * 1000 <= tNext) advance();
  };

  const next = () => advance();

  if (!open || !current) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", bgcolor: "#0f172a" } }}>
      <Box sx={{ position: "relative" }}>
        <Box sx={{ position: "relative", height: "70vh", bgcolor: "#000" }}>
          <TransitionStage sceneKey={index} plan={previewPlan}>
            {current.item_type === "TEMPLATE" ? (
              // Render the real template layout (zones / website / video /
              // widgets) — the SAME renderer the per-row preview uses — so the
              // playlist preview shows actual content, not a placeholder.
              <TemplatePreviewById
                templateId={current.template_id}
                fallbackName={current.template?.name}
              />
            ) : current.media?.media_type === "image" ? (
              <Box component="img" src={current.media?.url} alt=""
                sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
            ) : (
              <Box component="video" ref={videoRef as any}
                src={current.media?.url} autoPlay muted={false} controls={false}
                onEnded={next} onTimeUpdate={handleVideoTime}
                sx={{ width: "100%", height: "100%", display: "block", objectFit: "contain", bgcolor: "#000" }} />
            )}
          </TransitionStage>
          <IconButton onClick={onClose}
            sx={{
              position: "absolute", top: 12, right: 12, color: "#fff",
              bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{
            position: "absolute", top: 12, left: 12,
            display: "flex", gap: 1, alignItems: "center",
            bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(10px)",
            color: "#fff", px: 1.5, py: 0.5, borderRadius: 1.5,
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
              PREVIEW
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {playlistName}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar (timed items — video runs to its natural end) */}
        <Box sx={{ height: 4, bgcolor: "#1e293b" }}>
          <Box sx={{
            height: "100%",
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            width: isTimed ? `${progress}%` : "100%",
            transition: isTimed ? "width 100ms linear" : "none",
          }} />
        </Box>

        {/* Bottom controls / item list */}
        <Box sx={{
          background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff", p: 2,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
            <IconButton onClick={() => setPaused(p => !p)}
              sx={{
                color: NAVY_DARK,
                bgcolor: GOLD,
                "&:hover": { bgcolor: GOLD_SOFT },
              }}>
              {paused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
            <IconButton onClick={next}
              sx={{ color: "#cbd5e1", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.06)" } }}>
              <SkipNextIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {current.item_type === "TEMPLATE"
                  ? (current.template?.name ?? `Template #${current.template_id ?? ""}`)
                  : current.media?.original_name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                Item {index + 1} of {items.length} ·{" "}
                {isTimed
                  ? `${current.duration || DEFAULT_IMAGE_DURATION}s display`
                  : "plays to end"}
                {" · "}
                {effTransition.id === "cut" || effTransition.id === "none"
                  ? "Cut"
                  : `${transitionLabel(effTransition.id)} ${+(effTransition.ms / 1000).toFixed(2)}s`}
              </Typography>
            </Box>
            {current.media && <TypeBadge type={current.media.media_type} />}
          </Box>

          {/* Step dots */}
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
            {items.map((it, i) => (
              <Tooltip key={it.id} title={it.media?.original_name ?? ""}>
                <Box onClick={() => { setIndex(i); setProgress(0); }}
                  sx={{
                    width: i === index ? 24 : 8, height: 8, borderRadius: 4,
                    bgcolor: i === index ? GOLD : i < index ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.18)",
                    cursor: "pointer", transition: "all 0.2s ease",
                  }} />
              </Tooltip>
            ))}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

/* ──────────────────────────────────────────────────────────────────────── */
/* Duration dialog                                                            */
/* ──────────────────────────────────────────────────────────────────────── */

const DurationDialog: React.FC<{
  open: boolean;
  currentDuration: number;
  onClose: () => void;
  onConfirm: (s: number) => void;
}> = ({ open, currentDuration, onClose, onConfirm }) => {
  const [custom, setCustom] = useState<string>(String(currentDuration));

  useEffect(() => { if (open) setCustom(String(currentDuration)); }, [open, currentDuration]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          }}>
            <TimerIcon sx={{ fontSize: 16, color: "#d97706" }} />
          </Box>
          Display Duration
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 1.5 }}>
          How long should this image stay on screen?
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {DURATION_PRESETS.map(p => (
            <Button key={p} size="small"
              onClick={() => onConfirm(p)}
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 700,
                borderRadius: 2, px: 2, py: 0.75, minWidth: 64,
                border: "1px solid",
                borderColor: p === currentDuration ? GOLD : BORDER,
                bgcolor: p === currentDuration ? alpha(GOLD, 0.08) : "#fff",
                color: p === currentDuration ? NAVY : TEXT_PRIMARY,
                "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
              }}>
              {p}s
            </Button>
          ))}
        </Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Custom (seconds)
        </Typography>
        <TextField fullWidth size="small" type="number"
          inputProps={{ min: 1, max: 3600 }}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5, fontSize: 14, bgcolor: "#fff",
              "& fieldset": { borderColor: "#e2e8f0" },
              "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 2 },
            },
          }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation
          onClick={() => {
            const n = Math.max(1, Math.min(3600, parseInt(custom, 10) || DEFAULT_IMAGE_DURATION));
            onConfirm(n);
          }}
          sx={{
            borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
            color: NAVY_DARK,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
          }}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistEdit;
